import { Button, Group, Box, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import ImageUpload from "src/components/ImageUpload";
import {
  GetLocationsQuery,
  useCreateLocationServiceMutation,
  useGetRoomNamesQuery,
} from "src/generated/graphql";
import { memo } from "react";
import Textarea from "src/components/Textarea/Textarea";
import {
  IFormLocationService,
  locationServiceFormSchema,
  locationServiceInitialValues,
} from "./LocationServiceDetail";
import Switch from "src/components/Switch/Switch";
import NumberInput from "src/components/NumberInput/NumberInput";
import Select from "src/components/Select";
import { useModals } from "@mantine/modals";
import MultiSelect from "src/components/MultiSelect";

export interface IProps {
  filterLocations?: GetLocationsQuery["locationMany"] | undefined;
  handleGetOrganizationService: () => void;
  serviceId: string | undefined;
}

const CreateLocationService = ({
  filterLocations,
  handleGetOrganizationService,
  serviceId,
}: IProps) => {
  const theme = useMantineTheme();
  const modals = useModals();

  const form = useForm({
    initialValues: locationServiceInitialValues,
    validate: yupResolver(locationServiceFormSchema),
  });

  const { data: roomNames } = useGetRoomNamesQuery({
    variables: { locationId: form.getInputProps("location").value || "" },
    skip: !form.getInputProps("location").value,
  });

  const [createLocationServiceService, { loading }] =
    useCreateLocationServiceMutation({
      onCompleted: () => {
        modals.closeAll();
        handleGetOrganizationService();
      },
    });

  const handleSubmit = async (values: IFormLocationService) => {
    if (!serviceId) return;
    const { location, organizationService, rooms, ...rest } = values;
    createLocationServiceService({
      variables: {
        data: {
          ...rest,
          location: {
            id: location,
          },
          organizationService: {
            id: serviceId,
          },
          rooms: rooms.map((room) => ({ id: room })),
        },
      },
    });
  };

  return (
    <Box sx={{ maxWidth: 360 }} mx="auto">
      <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
        <Select
          data={
            filterLocations?.map((val) => ({
              value: val.id,
              label: val.name,
            })) || []
          }
          label="Select Location"
          {...form.getInputProps("location")}
        />

        <MultiSelect
          data={
            roomNames?.roomMany?.map((val) => ({
              value: val.id,
              label: val.name,
            })) || []
          }
          label="Select Rooms"
          {...form.getInputProps("rooms")}
          styles={{ root: { width: "100%" } }}
        />

        <Textarea label="Description" {...form.getInputProps("description")} />
        <Group
          position="apart"
          sx={{ justifyContent: "flex-start" }}
          mt="lg"
          ml={0}
          mr={0}
        >
          <Box w={"35%"} style={{ marginLeft: 0, paddingLeft: 0 }}>
            <ImageUpload
              onReadSuccess={(b64) => form.setFieldValue("imgUrl", b64)}
              src={form.values.imgUrl || ""}
              alt="LocationService Logo"
              height="100px"
              hideInnerText={true}
            />
          </Box>
          <Box w={"60%"}>
            <NumberInput
              withAsterisk
              precision={2}
              label="Cost"
              {...form.getInputProps("cost")}
            />
            <Group mt="lg">
              <Switch
                labelPosition="left"
                label="Pre-Paid"
                checked={!!form.getInputProps("isPrepay")?.value}
                {...form.getInputProps("isPrepay")}
              />

              <Switch
                labelPosition="left"
                label="Active"
                checked={!!form.getInputProps("isActive")?.value}
                {...form.getInputProps("isActive")}
              />
            </Group>
          </Box>
        </Group>

        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={() => modals.closeAll()}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            radius="md"
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            loading={loading}
          >
            Create
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default memo(CreateLocationService);
